import { useState } from "react";
import { AlertTriangle, ArrowLeft, ArrowRight, Check, CheckCircle2, Pencil, X } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { ApiError } from "../../api/client";
import {
  decideCorrection,
  type Correction,
  type CorrectionEdits,
  type ReviewItem,
} from "../../api/pqx";
import { MathText } from "../../components/MathText";
import { sortedOptions } from "../../lib/options";

/** The fields a correction can change, in the order shown. */
const FIELDS = ["question_text", "options", "correct_answer", "topic", "subtopic"] as const;
type Field = (typeof FIELDS)[number];

const LABELS: Record<Field, string> = {
  question_text: "Question text",
  options: "Options",
  correct_answer: "Answer",
  topic: "Topic",
  subtopic: "Subtopic",
};

/** A field's value as text, to show side by side. */
function shown(field: Field, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (field === "options") {
    return Object.entries(sortedOptions(value as Record<string, string>))
      .map(([k, v]) => `${k}. ${v}`)
      .join("\n");
  }
  return String(value);
}

/** The reviewer's version of the engine's correction, while editing it. */
interface EditDraft {
  question_text: string;
  options: string;
  correct_answer: string;
  topic: string;
  subtopic: string;
}

function editDraftOf(p: Correction): EditDraft {
  return {
    question_text: p.question_text ?? "",
    options: p.options ? JSON.stringify(sortedOptions(p.options), null, 2) : "",
    correct_answer: p.correct_answer ?? "",
    topic: p.topic ?? "",
    subtopic: p.subtopic ?? "",
  };
}

/**
 * The edits to send: only the fields the correction changes, as the reviewer
 * left them. Throws a message when the options aren't an object of text.
 */
export function editsFrom(p: Correction, d: EditDraft): CorrectionEdits {
  const edits: CorrectionEdits = {};
  if (p.question_text !== undefined) edits.question_text = d.question_text.trim();
  if (p.options !== undefined) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(d.options);
    } catch {
      throw new Error('The options must be JSON, like {"A": "…", "B": "…"}.');
    }
    if (
      !parsed ||
      typeof parsed !== "object" ||
      Array.isArray(parsed) ||
      Object.values(parsed).some((v) => typeof v !== "string")
    ) {
      throw new Error("The options must be letters with their text.");
    }
    edits.options = parsed as Record<string, string>;
  }
  if (p.correct_answer !== undefined) edits.correct_answer = d.correct_answer.trim().toUpperCase();
  if (p.topic !== undefined) edits.topic = d.topic.trim();
  if (p.subtopic !== undefined) edits.subtopic = d.subtopic.trim();
  return edits;
}

/**
 * One bank question the engine checked: what it is now beside the engine's
 * correction. Nothing changes on the question until the reviewer accepts;
 * students see the corrected version from then on.
 */
export function CorrectionReview({
  bookId,
  item,
  onChange,
}: {
  bookId: string;
  item: ReviewItem;
  onChange: (next: ReviewItem) => void;
}) {
  const navigate = useNavigate();
  const q = item.question;
  const proposal = q.proposal ?? null;
  const [editing, setEditing] = useState<EditDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The question changed since it was checked; the reviewer can accept anyway. */
  const [conflict, setConflict] = useState(false);

  const go = (id: string | null) => {
    if (id) navigate(`/books/${bookId}/review/${id}`);
  };
  const current: Record<Field, unknown> = {
    question_text: q.question_text,
    options: q.options,
    correct_answer: q.correct_answer,
    topic: q.topic,
    subtopic: q.subtopic,
  };
  const changed = proposal ? FIELDS.filter((f) => proposal[f] !== undefined) : [];

  const decide = async (action: "accept" | "keep", force = false) => {
    setBusy(true);
    setError(null);
    try {
      const edits =
        action === "accept" && editing && proposal ? editsFrom(proposal, editing) : undefined;
      const next = await decideCorrection(bookId, q.id, action, {
        ...(edits ? { edits } : {}),
        ...(force ? { force } : {}),
      });
      setConflict(false);
      setEditing(null);
      onChange(next);
      if (next.next_pending_id && next.next_pending_id !== q.id) go(next.next_pending_id);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setConflict(true);
      setError(e instanceof Error ? e.message : "That didn't work.");
    } finally {
      setBusy(false);
    }
  };

  const field =
    "w-full rounded-lg border border-border bg-surface px-3 py-2 text-[14px] text-text outline-none focus:border-accent";

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to={`/books/${bookId}`}
          className="inline-flex items-center gap-1 text-[12px] text-text-muted hover:text-text"
        >
          <ArrowLeft size={14} aria-hidden="true" /> Back to the check
        </Link>
        <p className="text-[12px] text-text-muted tabular-nums">
          Question {item.position} of {item.total} ·{" "}
          {item.remaining === 0 ? "every correction decided" : `${item.remaining} to decide`}
        </p>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <h1 className="text-[18px] font-bold lg:text-[22px]">Engine's check</h1>
        {q.decision === "accepted" ? (
          <span className="inline-flex items-center gap-1 rounded bg-positive/15 px-2 py-0.5 text-[12px] font-semibold text-positive">
            <CheckCircle2 size={12} aria-hidden="true" /> Correction accepted
          </span>
        ) : q.decision === "kept" ? (
          <span className="rounded bg-surface-high px-2 py-0.5 text-[12px] text-text-muted">
            {proposal ? "Kept as it was" : "Correct as it was"}
          </span>
        ) : (
          <span className="rounded bg-warning/15 px-2 py-0.5 text-[12px] font-semibold text-warning">
            To decide
          </span>
        )}
        <Link to={`/questions/${q.id}`} className="text-[12px] text-accent-light">
          Open in the full editor
        </Link>
      </div>

      {proposal?.review_reason ? (
        <p className="mt-4 flex items-start gap-2 rounded-lg bg-warning/10 px-4 py-3 text-[13px] text-warning">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>
            The engine couldn't fix this one itself: {proposal.review_reason}. Look at it in the
            full editor.
          </span>
        </p>
      ) : null}

      {!proposal ? (
        <p className="mt-4 rounded-lg bg-positive/10 px-4 py-3 text-[13px] text-positive">
          The engine found nothing to change on this question.
        </p>
      ) : (
        <>
          {proposal.changes.length ? (
            <section className="mt-4">
              <h2 className="text-[14px] font-semibold">What the engine changed</h2>
              <ul className="mt-1 list-disc pl-5 text-[13px]">
                {proposal.changes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </section>
          ) : null}

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <section className="rounded-xl border border-border bg-surface p-5">
              <h2 className="text-[14px] font-semibold">Now</h2>
              <p className="mt-2 text-[14px] leading-[1.5]">
                <MathText text={q.question_text} />
              </p>
              {changed
                .filter((f) => f !== "question_text")
                .map((f) => (
                  <Value key={f} label={LABELS[f]} text={shown(f, current[f])} />
                ))}
            </section>
            <section className="rounded-xl border border-accent/40 bg-accent/5 p-5">
              <h2 className="text-[14px] font-semibold">Engine's correction</h2>
              {editing ? (
                <div className="mt-2 flex flex-col gap-3">
                  {changed.map((f) => (
                    <label key={f} className="block">
                      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                        {LABELS[f]}
                      </span>
                      {f === "question_text" || f === "options" ? (
                        <textarea
                          rows={f === "options" ? 6 : 4}
                          value={editing[f]}
                          onChange={(e) => setEditing({ ...editing, [f]: e.target.value })}
                          className={field}
                        />
                      ) : (
                        <input
                          value={editing[f]}
                          onChange={(e) => setEditing({ ...editing, [f]: e.target.value })}
                          className={field}
                        />
                      )}
                    </label>
                  ))}
                </div>
              ) : (
                <>
                  <p className="mt-2 text-[14px] leading-[1.5]">
                    <MathText text={proposal.question_text ?? q.question_text} />
                  </p>
                  {changed
                    .filter((f) => f !== "question_text")
                    .map((f) => (
                      <Value key={f} label={LABELS[f]} text={shown(f, proposal[f])} />
                    ))}
                </>
              )}
            </section>
          </div>
        </>
      )}

      {conflict ? (
        <div
          role="alert"
          className="mt-4 rounded-lg bg-warning/10 px-4 py-3 text-[13px] text-warning"
        >
          <p>
            Someone changed this question after it was sent to be checked. Open it in the full
            editor to see what changed. Accepting anyway writes the engine's fields over theirs; the
            rest of their changes are kept.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => void decide("accept", true)}
            className="mt-2 font-semibold text-accent-light disabled:opacity-40"
          >
            Accept anyway
          </button>
        </div>
      ) : error ? (
        <p role="alert" className="mt-4 text-[13px] text-negative">
          {error}
        </p>
      ) : null}

      <div className="sticky bottom-0 mt-5 flex flex-wrap items-center gap-2 border-t border-border bg-bg py-3">
        <button
          type="button"
          disabled={!item.previous_id || busy}
          onClick={() => go(item.previous_id)}
          className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-2 text-[13px] disabled:opacity-40"
        >
          <ArrowLeft size={14} aria-hidden="true" /> Previous
        </button>
        <button
          type="button"
          disabled={!item.next_id || busy}
          onClick={() => go(item.next_id)}
          className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-2 text-[13px] disabled:opacity-40"
        >
          Next <ArrowRight size={14} aria-hidden="true" />
        </button>
        <span className="flex-1" />
        {proposal && !q.decision ? (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => void decide("keep")}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-[13px] font-semibold disabled:opacity-40"
            >
              <X size={14} aria-hidden="true" /> Keep as it is
            </button>
            {!editing && changed.length ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => setEditing(editDraftOf(proposal))}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-[13px] font-semibold disabled:opacity-40"
              >
                <Pencil size={14} aria-hidden="true" /> Edit the correction
              </button>
            ) : null}
            {changed.length ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => void decide("accept")}
                className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-bg disabled:opacity-50"
              >
                <Check size={14} aria-hidden="true" />
                {editing ? "Accept my version" : "Accept the correction"}
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

function Value({ label, text }: { label: string; text: string }) {
  return (
    <div className="mt-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">{label}</p>
      <p className="mt-0.5 whitespace-pre-line break-words text-[14px]">
        <MathText text={text} />
      </p>
    </div>
  );
}
